import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { TOAST_MAX_VISIBLE } from './catalog.js';
import { useConnectivityToasts } from './useConnectivityToasts.js';
import {
  dismissToast,
  subscribeToastDismiss,
  subscribeToasts,
  type ToastMessage,
} from './toast.js';

function toastLiveRegion(type: ToastMessage['type']): 'polite' | 'assertive' {
  return type === 'error' || type === 'offline' ? 'assertive' : 'polite';
}

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: (id: string) => void }) {
  const [paused, setPaused] = useState(false);
  const remainingRef = useRef(toast.durationMs);
  const lastTickRef = useRef(Date.now());

  useEffect(() => {
    remainingRef.current = toast.durationMs;
    lastTickRef.current = Date.now();
  }, [toast.id, toast.durationMs]);

  useEffect(() => {
    if (paused) {
      return;
    }

    const timer = window.setInterval(() => {
      const elapsed = Date.now() - lastTickRef.current;
      lastTickRef.current = Date.now();
      remainingRef.current -= elapsed;
      if (remainingRef.current <= 0) {
        onDismiss(toast.id);
      }
    }, 250);

    return () => window.clearInterval(timer);
  }, [paused, toast.id, onDismiss]);

  const pause = () => {
    if (!paused) {
      remainingRef.current -= Date.now() - lastTickRef.current;
      setPaused(true);
    }
  };

  const resume = () => {
    if (paused) {
      lastTickRef.current = Date.now();
      setPaused(false);
    }
  };

  return (
    <div
      role="status"
      aria-live={toastLiveRegion(toast.type)}
      data-toast-type={toast.type}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      style={{
        padding: 'var(--space-sm) var(--space-md)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-raised)',
        border: '1px solid var(--color-border-default)',
        boxShadow: 'var(--elevation-toast, 0 4px 12px rgba(0,0,0,0.12))',
        maxWidth: 360,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-xs)',
        zIndex: 400,
      }}
    >
      <span>{toast.message}</span>
      <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        {toast.action ? (
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick();
              onDismiss(toast.id);
            }}
            style={{
              minHeight: 44,
              padding: '0 var(--space-sm)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-border-default)',
              background: 'var(--color-surface-page)',
              cursor: 'pointer',
            }}
          >
            {toast.action.label}
          </button>
        ) : null}
        {toast.dismissible ? (
          <button
            type="button"
            aria-label="Dismiss notification"
            onClick={() => onDismiss(toast.id)}
            style={{
              minHeight: 44,
              padding: '0 var(--space-sm)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function ToastProvider({
  children,
  connectivityToasts = true,
}: {
  children: ReactNode;
  connectivityToasts?: boolean;
}) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  useConnectivityToasts(connectivityToasts);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  useEffect(() => {
    return subscribeToasts((toast) =>
      setToasts((prev) => [...prev, toast].slice(-TOAST_MAX_VISIBLE)),
    );
  }, []);

  useEffect(() => {
    return subscribeToastDismiss(removeToast);
  }, [removeToast]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && toasts.length > 0) {
        const latest = toasts[toasts.length - 1];
        if (latest?.dismissible) {
          dismissToast(latest.id);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toasts]);

  return (
    <>
      {children}
      <div
        aria-label="Notifications"
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 400,
        }}
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
        ))}
      </div>
    </>
  );
}
